import React, { useEffect, useState } from "react";
import { api, errorMessage } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/StatusBadge";
import { SALES_STATUS } from "@/lib/salesConstants";
import { formatKr, formatDate } from "@/lib/format";
import { Search, Receipt, FileText, Loader2, Link2 } from "lucide-react";
import { cn } from "@/lib/utils";

const COPY = {
  sales: {
    title: "Koble til faktura",
    description: "Velg den utgående fakturaen dette bilaget hører til. Søk på fakturanummer eller kundenavn.",
    placeholder: "Fakturanummer eller kunde …",
    endpoint: "/attachments/lookup/sales-invoices",
    empty: "Ingen fakturaer matcher søket.",
    icon: Receipt,
  },
  invoice: {
    title: "Koble til eksisterende regning",
    description: "Velg regningen i Regninger som dette bilaget dokumenterer. Søk på leverandør, fakturanummer eller KID.",
    placeholder: "Leverandør, fakturanr eller KID …",
    endpoint: "/attachments/lookup/invoices",
    empty: "Ingen regninger matcher søket.",
    icon: FileText,
  },
};

const SalesStatusPill = ({ status }) => {
  const cfg = SALES_STATUS[status];
  if (!cfg) return null;
  return <span className={cn("inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium", cfg.cls)}>{cfg.label}</span>;
};

/**
 * Dialog for å velge et eksisterende dokument å koble bilaget til.
 * kind = "sales" (utgående faktura i Faktura-modulen) | "invoice" (inngående regning i Regninger).
 */
export const LinkPickerDialog = ({ open, onOpenChange, kind = "sales", initialQuery = "", onPick, busy = false }) => {
  const cfg = COPY[kind] || COPY.sales;
  const [q, setQ] = useState(initialQuery);
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (open) { setQ(initialQuery || ""); setSelected(null); setError(""); }
  }, [open, initialQuery]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setItems(null);
    const t = setTimeout(async () => {
      try {
        const res = await api.get(cfg.endpoint, { params: { q: q.trim() || undefined, limit: 20 } });
        if (!cancelled) setItems(res.data.items || []);
      } catch (e) {
        if (!cancelled) { setError(errorMessage(e, "Kunne ikke søke")); setItems([]); }
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, q, cfg.endpoint]);

  // Prøv uten søkeord hvis det forhåndsutfylte søket ikke gir treff
  useEffect(() => {
    if (open && items && items.length === 0 && q && q === initialQuery && !error) setQ("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const Icon = cfg.icon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card sm:max-w-lg" data-testid={`link-picker-${kind}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Icon className="h-5 w-5 text-primary" /> {cfg.title}</DialogTitle>
          <DialogDescription>{cfg.description}</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={cfg.placeholder} className="pl-9" autoFocus aria-label="Søk" data-testid="link-picker-search" />
        </div>
        <div className="max-h-[320px] overflow-auto rounded-lg border divide-y" role="listbox" aria-label="Treff" data-testid="link-picker-results">
          {items === null ? (
            <div className="p-3 space-y-2"><Skeleton className="h-10" /><Skeleton className="h-10" /><Skeleton className="h-10" /></div>
          ) : error ? (
            <p className="p-4 text-sm text-destructive">{error}</p>
          ) : items.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground" data-testid="link-picker-empty">{cfg.empty}</p>
          ) : items.map((it) => {
            const isSel = selected?.id === it.id;
            return (
              <button
                key={it.id}
                type="button"
                role="option"
                aria-selected={isSel}
                onClick={() => setSelected(it)}
                onDoubleClick={() => onPick?.(it)}
                className={cn("w-full text-left px-3 py-2.5 flex items-center gap-3 hover:bg-secondary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", isSel && "bg-accent text-accent-foreground")}
                data-testid={`link-picker-item-${it.id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {kind === "sales" ? `${it.type === "kreditnota" ? "Kreditnota" : "Faktura"} ${it.number_display || "(kladd)"}${it.customer_name ? ` · ${it.customer_name}` : ""}` : (it.issuer_name || "Ukjent leverandør")}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {kind === "sales" ? `${it.issue_date ? formatDate(it.issue_date) : "Ikke utstedt"}` : `${it.invoice_number ? `Nr ${it.invoice_number} · ` : ""}Forfall ${formatDate(it.due_date)}`}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-medium tnum">{formatKr(kind === "sales" ? it.total : it.amount, it.currency)}</p>
                  {kind === "sales" ? <SalesStatusPill status={it.status} /> : <StatusBadge status={it.status} />}
                </div>
              </button>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="link-picker-cancel">Avbryt</Button>
          <Button onClick={() => selected && onPick?.(selected)} disabled={!selected || busy} data-testid="link-picker-confirm">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Link2 className="h-4 w-4 mr-2" />} Koble
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
